import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import {Message} from '@pb/kit-component-message';
import Typography from '@pb/kit-component-typography';

import {SUPPORT_EMAIL} from '../../utils/build-support-mailto';
import {
    MAX_DOMAINS,
    getSelectedDomains,
    isDomainSelected,
    isDuplicateDomain,
    isWarningPending,
    normalizeDomain,
    validateDomain,
} from '../../utils/domain-validation';


import {DomainInputRow} from './components/domain-input-row';
import {DomainListItem} from './components/domain-list-item';
import {useVerifyDomainApi} from './hooks/use-verify-domain-api';

import {domainLimitMessage, domainList, helperCode, helperText} from './domain-manager.module.styl';

import type {VerifyDomainData} from '../../utils/domain-validation';

type DomainManagerProps = {
    domainData: VerifyDomainData,
    onDomainDataChange: (data: VerifyDomainData) => void,
    busy?: boolean,
};

export const DomainManager: React.FC<DomainManagerProps> = ({domainData, onDomainDataChange, busy = false}) => {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState<string | null>(null);
    const {verifyDomain, loading: verifyingDomain} = useVerifyDomainApi();
    const {domainsAdd, domainVerificationComplete, domainVerificationStart} = useEventTracking();
    const [restoringWarnings, setRestoringWarnings] = useState(false);
    const latestDomainDataRef = useRef(domainData);
    const warningCheckedRef = useRef(new Set<string>());

    latestDomainDataRef.current = domainData;

    useEffect(() => {
        const pending = domainData.domains
            .filter(d => isWarningPending(d) && !warningCheckedRef.current.has(d.value))
            .map(d => d.value);
        if (pending.length === 0) {
            return;
        }
        pending.forEach(value => warningCheckedRef.current.add(value));
        setRestoringWarnings(true);

        Promise.all(pending.map(async value => ({value, result: await verifyDomain(value)})))
            .then(results => new Map(results.map(({value, result}) => [value, result.warning ?? null])))
            .catch(() => new Map<string, string | null>())
            .then(warnings => {
                const current = latestDomainDataRef.current;
                onDomainDataChange({
                    ...current,
                    domains: current.domains.map(d =>
                        pending.includes(d.value) && isWarningPending(d)
                            ? {...d, warning: warnings.get(d.value) ?? null}
                            : d,
                    ),
                });
                setRestoringWarnings(false);
            });
    }, [domainData.domains, verifyDomain, onDomainDataChange]);

    const handleSubmit = useCallback(async () => {
        const trimmed = normalizeDomain(inputValue);
        const result = validateDomain(trimmed);
        if (!result.valid) {
            setInputError(result.error);
            return;
        }
        if (isDuplicateDomain(trimmed, domainData.domains)) {
            setInputError('This domain has already been added.');
            return;
        }
        const isPrimary = domainData.domains.length === 0;
        domainVerificationStart({domain: trimmed});
        const apiResult = await verifyDomain(trimmed);
        domainVerificationComplete({domain: trimmed, isPrimary, valid: apiResult.valid});
        if (!apiResult.valid) {
            setInputError(apiResult.error);
            return;
        }
        domainsAdd({domain: trimmed, domainCount: domainData.domains.length + 1});
        onDomainDataChange({
            ...domainData,
            domains: [...domainData.domains, {value: trimmed, verified: true, warning: apiResult.warning ?? null}],
        });
        setInputValue('');
        setInputError(null);
    }, [
        inputValue,
        domainData,
        onDomainDataChange,
        verifyDomain,
        domainsAdd,
        domainVerificationComplete,
        domainVerificationStart,
    ]);

    const handleRemoveDomain = useCallback((index: number) => {
        const updated = domainData.domains.filter((_, i) => i !== index);
        onDomainDataChange({...domainData, domains: updated});
    }, [domainData, onDomainDataChange]);

    const handleVerifyDomain = useCallback(async (index: number) => {
        const domain = domainData.domains[index];
        const trimmedValue = normalizeDomain(domain.value);
        domainVerificationStart({domain: trimmedValue});
        const apiResult = await verifyDomain(trimmedValue);
        domainVerificationComplete({domain: trimmedValue, isPrimary: index === 0, valid: apiResult.valid});
        const updated = domainData.domains.map((d, i) =>
            i === index
                ? {...d, value: trimmedValue, verified: apiResult.valid, warning: apiResult.warning ?? null}
                : d,
        );
        onDomainDataChange({...domainData, domains: updated});
    }, [domainData, onDomainDataChange, verifyDomain, domainVerificationComplete, domainVerificationStart]);

    const handleToggleSelect = useCallback((index: number) => {
        const updated = domainData.domains.map((d, i) =>
            i === index ? {...d, selected: !isDomainSelected(d)} : d,
        );
        onDomainDataChange({...domainData, domains: updated});
    }, [domainData, onDomainDataChange]);

    const selectedDomains = getSelectedDomains(domainData.domains);
    const limitExceeded = domainData.domains.length > MAX_DOMAINS;
    const showSelection = limitExceeded || selectedDomains.length < domainData.domains.length;
    const disabled = busy || verifyingDomain || restoringWarnings;

    return (
        <>
            <DomainInputRow
                value={inputValue}
                error={inputError}
                disabled={disabled}
                onValueChange={value => {
                    setInputValue(value);
                    setInputError(null);
                }}
                onSubmit={handleSubmit}
            />
            <Typography variant="body-level-1" className={helperText}>
                Only enter the part after @ in your employees&apos; email addresses
                (e.g., enter <span className={helperCode}>acme.com</span> for user@acme.com).
            </Typography>

            {limitExceeded ? (
                <Message color="warning">
                    <span className={domainLimitMessage}>
                        Only {MAX_DOMAINS} domains can be saved ({selectedDomains.length} selected).
                        Deselect domains until {MAX_DOMAINS} or fewer remain.
                        Need to add more domains? Please email{' '}
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for more details.
                    </span>
                </Message>
            ) : null}

            {domainData.domains.length > 0 && (
                <div className={domainList}>
                    {domainData.domains.map((domain, index) => (
                        <DomainListItem
                            key={domain.value}
                            domain={domain}
                            checking={isWarningPending(domain)}
                            disabled={disabled}
                            selectable={showSelection}
                            selected={isDomainSelected(domain)}
                            onToggleSelect={() => handleToggleSelect(index)}
                            onRemove={() => handleRemoveDomain(index)}
                            onVerify={() => handleVerifyDomain(index)}
                        />
                    ))}
                </div>
            )}
        </>
    );
};
